const waitFor = (target, eventName, errorMessage) => new Promise((resolve, reject) => {
  const cleanup = () => {
    target.removeEventListener(eventName, onReady);
    target.removeEventListener('error', onError);
  };
  const onReady = () => { cleanup(); resolve(); };
  const onError = () => { cleanup(); reject(new Error(errorMessage)); };
  target.addEventListener(eventName, onReady, { once: true });
  target.addEventListener('error', onError, { once: true });
});

const seekTo = async (video, seconds) => {
  if (Math.abs(video.currentTime - seconds) < 0.03) return;
  const ready = waitFor(video, 'seeked', 'We could not read this part of the video.');
  video.currentTime = seconds;
  await ready;
};

export async function captureVideoStoryboard(file, options = {}) {
  if (!file) return null;
  const maxFrames = Math.max(6, Math.min(16, Number(options.maxFrames) || 14));
  const maxWidth = Math.max(320, Math.min(720, Number(options.maxWidth) || 640));
  const quality = Math.max(0.55, Math.min(0.82, Number(options.quality) || 0.7));
  const video = document.createElement('video');
  const objectUrl = URL.createObjectURL(file);
  video.muted = true;
  video.preload = 'metadata';
  video.playsInline = true;

  try {
    const metadataReady = waitFor(video, 'loadedmetadata', 'This video format could not be read. Try an MP4 video.');
    video.src = objectUrl;
    await metadataReady;

    const duration = Number(video.duration);
    if (!Number.isFinite(duration) || duration <= 0) throw new Error('This video has no readable duration. Try an MP4 video.');
    const frameCount = Math.min(maxFrames, Math.max(6, Math.ceil(duration * 1.5)));
    const sourceWidth = video.videoWidth || 1080;
    const sourceHeight = video.videoHeight || 1920;
    const width = Math.min(maxWidth, sourceWidth);
    const height = Math.max(1, Math.round(sourceHeight * (width / sourceWidth)));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('This browser could not prepare video frames.');

    const frames = [];
    for (let index = 0; index < frameCount; index += 1) {
      const timestamp = Math.min(duration - 0.02, Math.max(0, ((index + 0.35) / frameCount) * duration));
      await seekTo(video, timestamp);
      context.drawImage(video, 0, 0, width, height);
      frames.push({
        timestamp: Number(timestamp.toFixed(2)),
        dataUrl: canvas.toDataURL('image/jpeg', quality)
      });
    }

    return {
      duration: Number(duration.toFixed(2)),
      width: sourceWidth,
      height: sourceHeight,
      frames
    };
  } finally {
    video.removeAttribute('src');
    video.load();
    URL.revokeObjectURL(objectUrl);
  }
}
